import React from "react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white via-gray-50 to-blue-50 text-gray-900">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-gray-100 py-5 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Americar Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-semibold font-poppins text-blue-700">
              Terms of Service
            </span>
          </div>

          {/* Back Button */}
          <button
            onClick={() => navigate("/")}
            className="text-blue-600 hover:text-blue-800 font-medium transition-all"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-5xl mx-auto px-6 py-16 sm:py-24 space-y-8 leading-relaxed text-gray-800">
        <h1 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 text-center mb-10">
          Americar Terms of Service
        </h1>
        <p className="text-center text-gray-600 mb-8">
          <strong>Last updated:</strong> [Insert date before publishing]
        </p>

        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              1. Introduction
            </h2>
            <p>
              Welcome to <strong>Americar</strong> (“we,” “us,” or “our”). By
              downloading, accessing, or using the Americar mobile app (the
              “App”), you agree to these Terms of Service (“Terms”) and our
              Privacy Policy. If you do not agree, you may not use the App.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              2. About Americar
            </h2>
            <p>
              Americar provides vehicle maintenance tracking, roadside
              assistance, and vendor connection services for drivers through its
              mobile application available on the App Store and Google Play
              Store.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              3. Eligibility
            </h2>
            <p>
              You must be at least 18 years old and reside in the United States
              to use the Americar App. The App is not intended for minors.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              4. User Accounts
            </h2>
            <p>
              You are responsible for maintaining the confidentiality of your
              login credentials and for all activities that occur under your
              account. Americar reserves the right to suspend or terminate any
              account that violates these Terms or engages in unauthorized
              activity.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              5. Acceptable Use
            </h2>
            <p>When using Americar, you agree not to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Violate any applicable law or regulation.</li>
              <li>Submit false or misleading information.</li>
              <li>
                Attempt to gain unauthorized access to systems, data, or
                services.
              </li>
              <li>Use the platform for unlawful or fraudulent purposes.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              6. Service Providers
            </h2>
            <p>
              Americar connects users with third-party automotive vendors. These
              vendors operate independently and are not employees or agents of
              Americar. We are not responsible for their services, pricing, or
              performance.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              7. Payments
            </h2>
            <p>
              Payments made through Americar are securely processed by{" "}
              <strong>Stripe</strong>. Americar does not store or have access to
              full payment details. We are not responsible for issues arising
              from third-party payment processors.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              8. Liability Disclaimer
            </h2>
            <p>
              Americar is provided “as is” and “as available.” We make no
              warranties of any kind regarding reliability, availability, or
              suitability for a particular purpose. Americar is not liable for
              indirect, incidental, or consequential damages arising from your
              use of the App.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              9. Intellectual Property
            </h2>
            <p>
              All logos, trademarks, and content on Americar are the property of
              Americar or its licensors. You may not copy, distribute, modify,
              or reproduce any content without written consent.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              10. Termination
            </h2>
            <p>
              We may suspend or terminate your account at any time if you
              violate these Terms or misuse the platform. Upon termination, your
              right to access the App will immediately cease.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              11. Changes to These Terms
            </h2>
            <p>
              We may update these Terms from time to time. Any changes will be
              communicated through the App or our website. Continued use of
              Americar constitutes acceptance of the updated Terms.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              12. Contact Us
            </h2>
            <p>
              If you have questions about these Terms, contact us at:
              <br />
              📧{" "}
              <a
                href="mailto:support@americar.app"
                className="text-blue-600 hover:underline"
              >
                support@americar.app
              </a>
              <br />
              📧{" "}
              <a
                href="mailto:vroom@americar.app"
                className="text-blue-600 hover:underline"
              >
                vroom@americar.app
              </a>
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center">
        <p className="text-sm">
          © 2025 Americar. All rights reserved. | Terms of Service
        </p>
      </footer>
    </div>
  );
};

export default TermsOfService;
